import React, { useState, useEffect, useMemo } from 'react';
import JournalEntryHistoryPanel, { JournalSuggestion } from './JournalEntryHistoryPanel';
import { getErrorMessage } from '../utils/errorHandler';

interface Kamoku {
  id: number;
  kamoku: string;
  kamoku_code?: string;
}

interface GeneralLedgerTransaction {
  date: string;
  denpyou_no?: string;
  tekiyou: string;
  aite_kamoku?: string;
  karikata: number;
  kashikata: number;
  balance: number;
}

interface GeneralLedgerAccount {
  kamoku_id: number;
  kamoku_code: string;
  kamoku_name: string;
  opening_balance: number;
  closing_balance: number;
  transactions: GeneralLedgerTransaction[];
  transaction_count: number;
}

interface GeneralLedgerData {
  period_start: string;
  period_end: string;
  accounts: GeneralLedgerAccount[];
}

interface JournalHistorySidePanelProps {
  // 仕訳入力履歴用
  suggestions: JournalSuggestion[];
  suggestionLoading: boolean;
  suggestionError: string | null;
  accountOptions: Array<{ value: number | null; label: string }>;
  selectedAccountId: number | null;
  selectedAccountName?: string;
  onAccountChange: (accountId: number | null) => void;
  onSuggestionSelect: (suggestion: JournalSuggestion) => void;
  contextNote?: string;
  
  // 総勘定元帳用
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
  kamokuList: Kamoku[];
  
  // アクティブ行情報（総勘定元帳で使用）
  activeLineDebitAccountId?: number | null;
  activeLineCreditAccountId?: number | null;
}

type TabType = 'journal' | 'ledger';

const JournalHistorySidePanel: React.FC<JournalHistorySidePanelProps> = ({
  suggestions,
  suggestionLoading,
  suggestionError,
  accountOptions,
  selectedAccountId,
  selectedAccountName,
  onAccountChange,
  onSuggestionSelect,
  contextNote,
  apiFetch,
  currentTenantId,
  kamokuList,
  activeLineDebitAccountId,
  activeLineCreditAccountId,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('journal');
  const [ledgerData, setLedgerData] = useState<GeneralLedgerData | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [selectedLedgerAccountId, setSelectedLedgerAccountId] = useState<number | null>(null);
  const [periodStart, setPeriodStart] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // タブ切り替え時に総勘定元帳データを取得
  useEffect(() => {
    if (activeTab === 'ledger' && currentTenantId != null) {
      fetchGeneralLedger();
    }
  }, [activeTab, currentTenantId, selectedLedgerAccountId, periodStart, periodEnd]);

  // アクティブ行の勘定科目が変更されたら、総勘定元帳の表示科目も更新
  useEffect(() => {
    if (activeTab === 'ledger') {
      const targetAccountId = activeLineDebitAccountId || activeLineCreditAccountId;
      if (targetAccountId) {
        setSelectedLedgerAccountId(targetAccountId);
      }
    }
  }, [activeTab, activeLineDebitAccountId, activeLineCreditAccountId]);

  const fetchGeneralLedger = async () => {
    if (currentTenantId == null) {
      return;
    }

    setLedgerLoading(true);
    setLedgerError(null);

    try {
      const params = new URLSearchParams({
        period_start: periodStart,
        period_end: periodEnd,
      });
      
      if (selectedLedgerAccountId) {
        params.append('kamoku_id', selectedLedgerAccountId.toString());
      }

      const response = await apiFetch(`/api/general-ledger?${params.toString()}`);

      if (!response.ok) {
        const errorMessage = await getErrorMessage(response, '総勘定元帳の取得に失敗しました');
        throw new Error(errorMessage);
      }

      const data = await response.json();
      setLedgerData(data);
    } catch (err: any) {
      console.error('総勘定元帳取得エラー:', err);
      setLedgerError(err?.message || '総勘定元帳の取得に失敗しました');
      setLedgerData(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const ledgerAccountOptions = useMemo(() => {
    const options: Array<{ value: number | null; label: string }> = [
      { value: null, label: '全ての科目' },
    ];

    kamokuList.forEach((kamoku) => {
      options.push({
        value: kamoku.id,
        label: `${kamoku.kamoku_code} - ${kamoku.kamoku}`,
      });
    });

    return options;
  }, [kamokuList]);

  const displayedLedgerAccounts = useMemo(() => {
    if (!ledgerData) return [];
    
    if (selectedLedgerAccountId) {
      return ledgerData.accounts.filter(acc => acc.kamoku_id === selectedLedgerAccountId);
    }
    
    return ledgerData.accounts;
  }, [ledgerData, selectedLedgerAccountId]);

  const renderTabButton = (tabType: TabType, label: string) => {
    const isActive = activeTab === tabType;
    return (
      <button
        onClick={() => setActiveTab(tabType)}
        style={{
          padding: '6px 16px',
          backgroundColor: isActive ? '#0066cc' : '#c0c0c0',
          color: isActive ? '#ffffff' : '#000000',
          border: `2px ${isActive ? 'inset' : 'outset'} #c0c0c0`,
          fontSize: '11px',
          fontFamily: 'MS UI Gothic, sans-serif',
          cursor: 'pointer',
          fontWeight: isActive ? 'bold' : 'normal',
        }}
      >
        {label}
      </button>
    );
  };

  const renderGeneralLedger = () => {
    if (ledgerLoading) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', fontStyle: 'italic' }}>
          総勘定元帳を読み込み中です...
        </div>
      );
    }

    if (ledgerError) {
      return (
        <div
          style={{
            padding: '12px',
            textAlign: 'center',
            color: '#cc0000',
            backgroundColor: '#ffe0e0',
            border: '1px solid #cc0000',
            margin: '10px',
          }}
        >
          エラー: {ledgerError}
        </div>
      );
    }

    if (!ledgerData || displayedLedgerAccounts.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          表示できるデータがありません
        </div>
      );
    }

    return (
      <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
        {displayedLedgerAccounts.map((account) => (
          <div
            key={account.kamoku_id}
            style={{
              marginBottom: '16px',
              border: '1px solid #c0c0c0',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                backgroundColor: '#e0e0e0',
                padding: '6px 8px',
                fontWeight: 'bold',
                fontSize: '11px',
                borderBottom: '1px solid #c0c0c0',
              }}
            >
              {account.kamoku_code} - {account.kamoku_name}
            </div>
            <div style={{ padding: '4px 8px', fontSize: '10px', backgroundColor: '#f5f5f5' }}>
              期首残高: ¥{account.opening_balance.toLocaleString('ja-JP')} | 
              期末残高: ¥{account.closing_balance.toLocaleString('ja-JP')} | 
              取引件数: {account.transaction_count}件
            </div>
            {account.transactions.length > 0 && (
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '10px',
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: '#d0d0d0' }}>
                    <th style={{ padding: '4px', borderBottom: '1px solid #808080' }}>日付</th>
                    <th style={{ padding: '4px', borderBottom: '1px solid #808080' }}>摘要</th>
                    <th style={{ padding: '4px', borderBottom: '1px solid #808080', textAlign: 'right' }}>
                      借方
                    </th>
                    <th style={{ padding: '4px', borderBottom: '1px solid #808080', textAlign: 'right' }}>
                      貸方
                    </th>
                    <th style={{ padding: '4px', borderBottom: '1px solid #808080', textAlign: 'right' }}>
                      残高
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {account.transactions.map((txn, idx) => (
                    <tr
                      key={`${account.kamoku_id}-${idx}`}
                      style={{
                        backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f7f7f7',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#e3eaff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#f7f7f7';
                      }}
                    >
                      <td style={{ padding: '4px', borderBottom: '1px solid #dcdcdc' }}>
                        {txn.date}
                      </td>
                      <td style={{ padding: '4px', borderBottom: '1px solid #dcdcdc' }}>
                        {txn.tekiyou || '-'}
                      </td>
                      <td
                        style={{
                          padding: '4px',
                          borderBottom: '1px solid #dcdcdc',
                          textAlign: 'right',
                        }}
                      >
                        {txn.karikata > 0 ? `¥${txn.karikata.toLocaleString('ja-JP')}` : '-'}
                      </td>
                      <td
                        style={{
                          padding: '4px',
                          borderBottom: '1px solid #dcdcdc',
                          textAlign: 'right',
                        }}
                      >
                        {txn.kashikata > 0 ? `¥${txn.kashikata.toLocaleString('ja-JP')}` : '-'}
                      </td>
                      <td
                        style={{
                          padding: '4px',
                          borderBottom: '1px solid #dcdcdc',
                          textAlign: 'right',
                          fontWeight: 'bold',
                        }}
                      >
                        ¥{txn.balance.toLocaleString('ja-JP')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundColor: '#f0f0f0',
        border: '2px inset #f0f0f0',
        padding: '0',
      }}
    >
      {/* タブボタン */}
      <div
        style={{
          display: 'flex',
          backgroundColor: '#d0d0d0',
          borderBottom: '2px solid #808080',
        }}
      >
        {renderTabButton('journal', '仕訳入力履歴')}
        {renderTabButton('ledger', '総勘定元帳')}
      </div>

      {/* タブコンテンツ */}
      {activeTab === 'journal' && (
        <JournalEntryHistoryPanel
          title="仕訳履歴候補"
          loading={suggestionLoading}
          error={suggestionError}
          suggestions={suggestions}
          accountOptions={accountOptions}
          selectedAccountId={selectedAccountId}
          selectedAccountName={selectedAccountName}
          onAccountChange={onAccountChange}
          onSelect={onSuggestionSelect}
          contextNote={contextNote}
        />
      )}

      {activeTab === 'ledger' && (
        <div>
          <div
            style={{
              backgroundColor: '#0066cc',
              color: '#ffffff',
              padding: '8px 12px',
              fontWeight: 'bold',
              fontSize: '11px',
            }}
          >
            総勘定元帳
          </div>
          
          {/* 期間選択・科目選択 */}
          <div
            style={{
              padding: '8px 12px',
              fontSize: '11px',
              backgroundColor: '#f0f0f0',
              borderBottom: '1px solid #c0c0c0',
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '70px' }}>期間開始</span>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                style={{
                  padding: '2px 4px',
                  border: '2px inset #c0c0c0',
                  fontSize: '11px',
                  fontFamily: 'MS UI Gothic, sans-serif',
                  backgroundColor: '#ffffff',
                  flex: 1,
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '70px' }}>期間終了</span>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                style={{
                  padding: '2px 4px',
                  border: '2px inset #c0c0c0',
                  fontSize: '11px',
                  fontFamily: 'MS UI Gothic, sans-serif',
                  backgroundColor: '#ffffff',
                  flex: 1,
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '70px' }}>表示科目</span>
              <select
                value={selectedLedgerAccountId ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedLedgerAccountId(value === '' ? null : Number(value));
                }}
                style={{
                  padding: '2px 4px',
                  border: '2px inset #c0c0c0',
                  fontSize: '11px',
                  fontFamily: 'MS UI Gothic, sans-serif',
                  backgroundColor: '#ffffff',
                  flex: 1,
                }}
              >
                {ledgerAccountOptions.map((option) => (
                  <option key={`ledger-option-${option.value ?? 'all'}`} value={option.value ?? ''}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* 総勘定元帳データ表示 */}
          {renderGeneralLedger()}
        </div>
      )}
    </div>
  );
};

export default JournalHistorySidePanel;
